/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useCallback, useMemo, useRef } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { expVal } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	VIEW_PAGE,
	VIEW_BLOG,
	VIEW_BLOG_DATE_LEGACY,
} from '@confluence/named-routes/entry-points/routes.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import { isExpectedError } from '../../utils/isExpectedError.ts';
import {
	OnboardingKeysCombined,
	type OnboardingKeysCombinedType,
} from '../../constants/onboarding-keys-combined.ts';
import { filterOnboardingState } from '../../utils/filterOnboardingState.ts';

import onboardingStateGetOnboardingStateQuery, {
	type onboardingStateGetOnboardingStateQuery$data,
	type onboardingStateGetOnboardingStateQuery$variables,
} from './__generated__/onboardingStateGetOnboardingStateQuery.graphql.ts';
import type {
	OnboardingStateInput,
	onboardingStateSetOnboardingStateMutation as onboardingStateSetOnboardingStateMutation$data,
	onboardingStateSetOnboardingStateMutationVariables as onboardingStateSetOnboardingStateMutation$variables,
} from './__types__/onboardingStateSetOnboardingStateMutation.ts';

// Relay generated types are immutable (readonly) whereas Apollo expects mutable types
type OnboardingStateData = {
	onboardingState?: readonly { key: string; value: string | null | undefined }[] | null;
};

const isOnboardingStateEqual = (
	a: readonly { key: string; value: string | null | undefined }[] | null | undefined,
	b: readonly { key: string; value: string | null | undefined }[] | null | undefined,
): boolean => {
	if (a === b) return true;
	if (!a || !b) return !a && !b;
	if (a.length !== b.length) return false;
	return a.every((item, i) => item.key === b[i].key && item.value === b[i].value);
};

// Hook to check if we're on a view page route
const useIsViewPageRoute = (): boolean =>
	useRouteName({
		selector: (routeName: string | undefined) =>
			routeName === VIEW_PAGE.name ||
			routeName === VIEW_BLOG.name ||
			routeName === VIEW_BLOG_DATE_LEGACY.name,
	});

// Get the combined onboarding state from cache, returns null if not cached
const getCombinedQueryFromCache = (): onboardingStateGetOnboardingStateQuery$data | null => {
	try {
		return readQueryInterop<
			onboardingStateGetOnboardingStateQuery$data,
			onboardingStateGetOnboardingStateQuery$variables
		>({
			query: onboardingStateGetOnboardingStateQuery,
			variables: { key: OnboardingKeysCombined as unknown as string[] },
		});
	} catch {
		return null;
	}
};

// Updates the combined query cache with new onboarding state values.
// This ensures components using the centralized cache see updates on next mount/navigation.
const updateCombinedQueryCache = (
	updatedStates: readonly { key: string; value: string | null }[],
): void => {
	if (!expVal('confluence_centralized_onboarding_state', 'isEnabled', false)) {
		return;
	}

	const currentData = getCombinedQueryFromCache();
	if (!currentData?.onboardingState) {
		return;
	}

	const updatedMap = new Map(updatedStates.map((s) => [s.key, s.value]));

	const newOnboardingState = currentData.onboardingState.map((item) =>
		updatedMap.has(item.key) ? { ...item, value: updatedMap.get(item.key) ?? item.value } : item,
	);

	writeQueryInterop({
		query: onboardingStateGetOnboardingStateQuery,
		variables: { key: OnboardingKeysCombined as unknown as string[] },
		data: { onboardingState: newOnboardingState },
	});
};

export const deserializeState = (
	data: onboardingStateGetOnboardingStateQuery$data | OnboardingStateData | null | undefined,
) => {
	const state = {} as any;
	// new graphQL call returns onboardingState array
	const { onboardingState } = data || {};

	// parse data and populate state accordingly
	if (onboardingState !== undefined && onboardingState !== null) {
		for (const { key, value } of onboardingState) {
			// key comes back as "{prefix}:{property}" format
			// split by ':' to get parsed {property}
			// some keys don't have a prefix, which is why we have the ||
			const parsedKey = key.split(':')[1] || key;
			if (!value) {
				continue;
			}
			if (/(true|false)/g.test(value)) {
				state[parsedKey] = value === 'true';
			} else {
				state[parsedKey] = value;
			}
		}
	}
	return state;
};

export const useSetOnboardingState = () => {
	const [setState, { error, called }] = useMutation<
		onboardingStateSetOnboardingStateMutation$data,
		onboardingStateSetOnboardingStateMutation$variables
	>(
		gql`
			mutation onboardingStateSetOnboardingStateMutation($states: [OnboardingStateInput!]!) {
				setOnboardingState(states: $states) {
					key
					value
				}
			}
		`,
		{
			update(cache, { data }) {
				const states = data!.setOnboardingState;
				states.forEach(({ key, value }) => {
					// writeFragment can only handle a single data item at a time
					cache.writeFragment({
						id: `OnboardingState:${key}`,
						fragment: gql`
							fragment myKey on OnboardingState {
								key
								value
							}
						`,
						data: {
							key,
							value,
							__typename: 'OnboardingState',
						},
					});
				});

				// Update the combined query cache for centralized onboarding state
				updateCombinedQueryCache(states);
			},
			onError: (error) => {
				if (isExpectedError(error)) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	type SetOnboardingStateArgs = OnboardingStateInput | OnboardingStateInput[];

	const setOnboardingState = useCallback(
		(newOnboardingState: SetOnboardingStateArgs) => {
			let states: OnboardingStateInput[];
			if (Array.isArray(newOnboardingState)) {
				// supports batch updates
				states = newOnboardingState;
			} else {
				// backwards compability for single key-value pair
				states = [newOnboardingState];
			}

			void setState({
				variables: {
					states,
				},
				optimisticResponse: {
					setOnboardingState: states.map(({ key, value }) => ({
						key,
						value,
						__typename: 'OnboardingState',
					})),
				},
			});
		},
		[setState],
	);

	return { setOnboardingState, error, called };
};

// safeguards against empty arrays or strings
const hasValidKeysToFetch = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
): boolean => {
	if (Array.isArray(key)) return key.length > 0;
	if (typeof key === 'string') return key.length > 0;
	return false;
};

/**
 * TODO: Migrate to fetch from userPreferences - this is considered deprecated
 *
 * @param key - The onboarding state key(s) to fetch
 * @param skip - Whether to skip the query
 * @param reactive - When true (default), subscribes to centralized cache changes for immediate updates when
 * setOnboardingState is called. No additional network requests are made.
 * When false: reads from centralized cache once, sees updates on next navigation/mount.
 */
export const useGetOnboardingState = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
	skip = false,
	reactive = true,
) => {
	const { isLicensed, isGuest } = useAccessStatus();
	const isAllowed = isLicensed || isGuest;
	const isViewPageRoute = useIsViewPageRoute();

	const hasKeysToFetch = hasValidKeysToFetch(key);
	const skipQuery = skip || !isAllowed || !hasKeysToFetch;

	// Check if centralized cache should be used (experiment enabled + on view page route)
	const useCentralizedCache =
		isViewPageRoute && expVal('confluence_centralized_onboarding_state', 'isEnabled', false);

	// For non-reactive mode: read from cache once (no subscription)
	// This is a one-time synchronous read that doesn't trigger re-renders on cache changes
	const cachedCombinedData = !reactive && useCentralizedCache ? getCombinedQueryFromCache() : null;
	const hasCachedCombinedData = cachedCombinedData !== null;

	// For reactive mode: use useQueryInterop with cache-only to subscribe to cache changes
	// This subscribes to the centralized cache and re-renders when it changes (no network requests)
	const {
		data: reactiveCacheData,
		error: reactiveCacheError,
		loading: reactiveCacheLoading,
	} = useQueryInterop<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(onboardingStateGetOnboardingStateQuery, {
		variables: { key: OnboardingKeysCombined as unknown as string[] },
		skip: skipQuery || !reactive || !useCentralizedCache,
		fetchPolicy: 'cache-only',
	});

	// Fallback query: used when centralized cache is not available or not applicable
	// This fetches individual keys from the network
	const {
		data: fallbackQueryData,
		error: fallbackError,
		loading: fallbackLoading,
		refetch,
	} = useQueryInterop<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(onboardingStateGetOnboardingStateQuery, {
		variables: { key: key as unknown as string[] },
		skip:
			skipQuery ||
			hasCachedCombinedData ||
			(reactive && useCentralizedCache && !!reactiveCacheData),
	});

	// Determine which data source to use:
	// 1. Non-reactive with cached data: use cachedCombinedData (filtered)
	// 2. Reactive with centralized cache: use reactiveCacheData (filtered)
	// 3. Fallback: use fallbackQueryData
	// For reactive path: skip re-render if filtered data for our keys hasn't changed
	const prevFilteredDataRef = useRef<onboardingStateGetOnboardingStateQuery$data | undefined>(
		undefined,
	);
	const data = useMemo(() => {
		if (hasCachedCombinedData) {
			return filterOnboardingState(cachedCombinedData, key);
		}
		if (reactive && useCentralizedCache && reactiveCacheData) {
			const filtered = filterOnboardingState(reactiveCacheData, key);
			if (
				filtered &&
				prevFilteredDataRef.current &&
				isOnboardingStateEqual(
					prevFilteredDataRef.current.onboardingState,
					filtered.onboardingState,
				)
			) {
				return prevFilteredDataRef.current;
			}
			prevFilteredDataRef.current = filtered;
			return filtered;
		}
		return fallbackQueryData;
	}, [
		hasCachedCombinedData,
		cachedCombinedData,
		reactive,
		useCentralizedCache,
		reactiveCacheData,
		fallbackQueryData,
		key,
	]);

	// Combine error and loading states from both queries
	const error = reactiveCacheError || fallbackError;
	const loading =
		(reactive && useCentralizedCache ? reactiveCacheLoading : false) || fallbackLoading;

	return {
		data,
		error,
		loading,
		refetch,
	};
};
